import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import SearchBar from "../../../components/searchbar/SearchBar";
import { getAllClients, getAllTasks } from "../../../redux/actions/authActions";
import { Box, Typography, Button } from "@mui/material";

const ReportsHome = () => {
  const dispatch = useDispatch();// eslint-disable-next-line
  const navigate = useNavigate();

  const clients = useSelector((state) => state.auth.allClients);
  const tasks = useSelector((state) => state.auth.allTasks);

  const [rows, setRows] = useState([]);
  const [tasksInProgress, setTasksInProgress] = useState(0);
  const [tasksCompleted, setTasksCompleted] = useState(0);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (token) {
      dispatch(getAllClients(token));
      dispatch(getAllTasks(token));
    }
  }, [dispatch, token]);

  useEffect(() => {
    if (Array.isArray(clients)) {
      setRows(clients);
    }
  }, [clients]);

  useEffect(() => {
    if (Array.isArray(tasks)) {
      setTasksInProgress(tasks.filter(t => t.status === "InProgress" || t.status === "Pending").length);
      setTasksCompleted(tasks.filter(t => t.status === "Completed").length);
    }
  }, [tasks]);

  const handleSearch = (searchTerm) => {
    const filtered = (clients || []).filter((client) =>
      client.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setRows(filtered);
  };

  // ── SHARED STATUS CELL ───────────────────────────────────────────────────────
  
  // ── TEAM ESCALATION COLUMNS ──────────────────────────────────────────────────
  // Use real field names that exist in the data + renderCell with params.row for nested values
  const teamEscalationColumns = [
    {
      field: "service",                    // ✅ Real field in data
      headerName: "Task Name",
      width: 220,
      renderCell: (params) => {
        const row = params.row;
        const name = row?.service?.name ||
          (row?.name && row.name.toLowerCase() !== "task" ? row.name : null) ||
          row?.id ||
          "Untitled Task";
        return <Typography variant="body2" fontWeight={500}>{name}</Typography>;
      },
    },
    {
      field: "client",                     // ✅ Real field in data (populated)
      headerName: "Client",
      width: 180,
      renderCell: (params) => {
        const client = params.row?.client;
        return (
          <Typography variant="body2">
            {client?.businessName || client?.name || "Unknown Client"}
          </Typography>
        );
      },
    },
    {
      field: "assignees",                  // ✅ Real field in data (populated array)
      headerName: "Assigned To",
      width: 200,
      renderCell: (params) => {
        const assignees = params.row?.assignees || [];
        return (
          <Typography variant="body2" sx={{ color: "#1e40af" }}>
            {assignees.map(a => a.name).join(", ") || "—"}
          </Typography>
        );
      },
    },
    {
      field: "teamDelayReason",            // ✅ Correct separated field
      headerName: "Team Escalation – Reason for Delay",
      width: 320,
      renderCell: (params) =>
        params.value ? (
          <Box sx={{ bgcolor: "#fff1f1", border: "1px solid #fca5a5", borderLeft: "4px solid #ef4444", borderRadius: 1, px: 1.5, py: 0.75, width: "100%" }}>
            <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
              🚨 {params.value}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic" }}>No delay reported</Typography>
        ),
    },
    {
      field: "teamRemarks",
      headerName: "Team Remarks",
      width: 240,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontStyle: "italic", color: params.value ? "#166534" : "text.secondary" }}>
          {params.value || "—"}
        </Typography>
      ),
    },
    {
      field: "view",
      headerName: "Action",
      width: 120,
      renderCell: (params) => (
        <Button 
          variant="contained" 
          size="small" 
          sx={{ bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" }, borderRadius: 2, textTransform: "none", fontWeight: "bold" }}
          onClick={() => navigate(`/reports/view-task/${params.row._id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  // ── CLIENT FEEDBACK COLUMNS ───────────────────────────────────────────────────
  const clientFeedbackColumns = [
    {
      field: "service",                    // ✅ Real field in data
      headerName: "Task Name",
      width: 220,
      renderCell: (params) => {
        const row = params.row;
        const name = row?.service?.name ||
          (row?.name && row.name.toLowerCase() !== "task" ? row.name : null) ||
          row?.id ||
          "Untitled Task";
        return <Typography variant="body2" fontWeight={500}>{name}</Typography>;
      },
    },
    {
      field: "client",                     // ✅ Real field in data (populated)
      headerName: "Client",
      width: 180,
      renderCell: (params) => {
        const client = params.row?.client;
        return (
          <Typography variant="body2">
            {client?.businessName || client?.name || "Unknown Client"}
          </Typography>
        );
      },
    },
    {
      field: "clientDelayReason",          // ✅ Correct separated field
      headerName: "Client Escalation – Reported Delay",
      width: 320,
      renderCell: (params) =>
        params.value ? (
          <Box sx={{ bgcolor: "#fff7ed", border: "1px solid #fdba74", borderLeft: "4px solid #f97316", borderRadius: 1, px: 1.5, py: 0.75, width: "100%" }}>
            <Typography variant="body2" sx={{ color: "#c2410c", fontWeight: 500 }}>
              📢 {params.value}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic" }}>No escalation reported</Typography>
        ),
    },
    {
      field: "clientRemarks",
      headerName: "Client Feedback / Remarks",
      width: 240,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontStyle: "italic", color: params.value ? "#1e3a5f" : "text.secondary" }}>
          {params.value || "No feedback yet"}
        </Typography>
      ),
    },
    {
      field: "view",
      headerName: "Action",
      width: 120,
      renderCell: (params) => (
        <Button 
          variant="contained" 
          size="small" 
          sx={{ bgcolor: "#f97316", "&:hover": { bgcolor: "#ea580c" }, borderRadius: 2, textTransform: "none", fontWeight: "bold" }}
          onClick={() => navigate(`/reports/view-task/${params.row._id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  // ── FILTERED DATA ─────────────────────────────────────────────────────────────
  // Team: only tasks where a team delay reason was explicitly filed
  const teamEscalations = Array.isArray(tasks)
    ? tasks.filter((t) => t.teamDelayReason && t.teamDelayReason.trim() !== "")
    : [];

  // Client: tasks where client filed an escalation or any remark
  const clientFeedback = Array.isArray(tasks)
    ? tasks.filter(
        (t) =>
          (t.clientDelayReason && t.clientDelayReason.trim() !== "") ||
          (t.clientRemarks && t.clientRemarks.trim() !== "")
      )
    : [];

  const clientColumns = [];

  return (
    <>
      {/* ── STATS BADGES ─────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
        <Box sx={{ flex: "1 1 160px", bgcolor: "#fff1f1", border: "1px solid #fca5a5", borderRadius: 2, p: 2 }}>
          <Typography variant="caption" color="error" sx={{ fontWeight: 700, textTransform: "uppercase", display: "block" }}>
            🚨 Team Escalations
          </Typography>
          <Typography variant="h4" color="error" sx={{ fontWeight: 800 }}>{teamEscalations.length}</Typography>
          <Typography variant="caption" color="textSecondary">Active delay reports</Typography>
        </Box>

        <Box sx={{ flex: "1 1 160px", bgcolor: "#fff7ed", border: "1px solid #fdba74", borderRadius: 2, p: 2 }}>
          <Typography variant="caption" sx={{ color: "#c2410c", fontWeight: 700, textTransform: "uppercase", display: "block" }}>
            📢 Client Reports
          </Typography>
          <Typography variant="h4" sx={{ color: "#c2410c", fontWeight: 800 }}>{clientFeedback.length}</Typography>
          <Typography variant="caption" color="textSecondary">Escalations from clients</Typography>
        </Box>

        <Box sx={{ flex: "1 1 160px", bgcolor: "#f0fdf4", border: "1px solid #86efac", borderRadius: 2, p: 2 }}>
          <Typography variant="caption" sx={{ color: "#166534", fontWeight: 700, textTransform: "uppercase", display: "block" }}>
            ✅ Completed
          </Typography>
          <Typography variant="h4" sx={{ color: "#166534", fontWeight: 800 }}>{tasksCompleted}</Typography>
          <Typography variant="caption" color="textSecondary">Finished tasks</Typography>
        </Box>

        <Box sx={{ flex: "1 1 160px", bgcolor: "#eff6ff", border: "1px solid #93c5fd", borderRadius: 2, p: 2 }}>
          <Typography variant="caption" sx={{ color: "#1e40af", fontWeight: 700, textTransform: "uppercase", display: "block" }}>
            ⏳ In Progress
          </Typography>
          <Typography variant="h4" sx={{ color: "#1e40af", fontWeight: 800 }}>{tasksInProgress}</Typography>
          <Typography variant="caption" color="textSecondary">Active & pending</Typography>
        </Box>
      </div>

      <div className="mt-8">
        {/* ── TEAM ESCALATIONS TABLE ────────────────────────────────────────── */}
        <Box sx={{ mb: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <Typography variant="h5" color="error" sx={{ fontWeight: "bold", m: 0 }}>
              Team Escalations &amp; Work Delays
            </Typography>
            {teamEscalations.length > 0 && (
              <Box sx={{ bgcolor: "#ef4444", color: "white", borderRadius: 10, px: 1.5, py: 0.25, fontSize: 12, fontWeight: 700 }}>
                {teamEscalations.length} Active
              </Box>
            )}
          </div>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Critical alerts raised by team members regarding internal bottlenecks or work delays.
          </Typography>
          <div style={{ height: 420, width: "100%" }}>
            <DataGrid
              rows={teamEscalations}
              columns={teamEscalationColumns}
              getRowId={(row) => row._id}
              pageSizeOptions={[5, 8, 10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 8, page: 0 } } }}
              pagination
              rowHeight={76}
              sx={{
                bgcolor: "white",
                borderRadius: 2,
                "& .MuiDataGrid-columnHeaders": { backgroundColor: "#fef2f2" },
                "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" }
              }}
              localeText={{ noRowsLabel: "✅ No team escalations reported" }}
            />
          </div>
        </Box>

        {/* ── CLIENT FEEDBACK TABLE ─────────────────────────────────────────── */}
        <Box sx={{ mb: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <Typography variant="h5" color="primary" sx={{ fontWeight: "bold", m: 0 }}>
              Client Feedback &amp; Reports
            </Typography>
            {clientFeedback.length > 0 && (
              <Box sx={{ bgcolor: "#f97316", color: "white", borderRadius: 10, px: 1.5, py: 0.25, fontSize: 12, fontWeight: 700 }}>
                {clientFeedback.length} Reports
              </Box>
            )}
          </div>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Escalations and feedback received directly from clients regarding task delays or concerns.
          </Typography>
          <div style={{ height: 420, width: "100%" }}>
            <DataGrid
              rows={clientFeedback}
              columns={clientFeedbackColumns}
              getRowId={(row) => row._id}
              pageSizeOptions={[5, 8, 10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 8, page: 0 } } }}
              pagination
              rowHeight={76}
              sx={{
                bgcolor: "white",
                borderRadius: 2,
                "& .MuiDataGrid-columnHeaders": { backgroundColor: "#fff7ed" },
                "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" }
              }}
              localeText={{ noRowsLabel: "✅ No client escalations reported" }}
            />
          </div>
        </Box>

        {/* ── CLIENTS OVERVIEW TABLE ────────────────────────────────────────── */}
        <Box sx={{ mt: 8 }}>
          <SearchBar titleOne="Registered Clients Overview" onSearch={handleSearch} />
          <div style={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={clientColumns}
              getRowId={(row) => row._id}
              pageSizeOptions={[5, 8, 10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 8, page: 0 } } }}
              pagination
              sx={{ 
                bgcolor: "white", 
                borderRadius: 2,
                "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" }
              }}
            />
          </div>
        </Box>
      </div>
    </>
  );
};

export default ReportsHome;
